/**
 * usageTracker — batched, silent usage-event capture.
 *
 * Public API (re-exported from @bridge2ai/core/frontend):
 *   track(action, props?)    — curated action event
 *   trackInput(field, value) — content capture for designated fields only
 *
 * Page views + time-on-page are automatic once initUsageTracker(router)
 * runs (createApp does this): the module id comes from route meta, so
 * tenant customModules are covered with zero extra work.
 *
 * Failure policy: silent. Nothing here may throw into app code, block
 * the UI, or surface errors to users. Flush failures drop the batch.
 * Disable entirely with VITE_USAGE_TRACKING=off.
 */
import type { Router } from 'vue-router'
import { getAuthToken } from '../utils/auth'
import { startPage, setVisibility, closePage, type PageTimer } from './pageTimer'

const MAX_QUEUE = 200
const FLUSH_MS = 30_000
const sessionId = Math.random().toString(36).slice(2, 14)

let queue: Record<string, unknown>[] = []
let timer: PageTimer = startPage(null, Date.now(), true)
let currentModule: string | null = null
let installed = false

function enqueue(evt: Record<string, unknown>): void {
  if (queue.length >= MAX_QUEUE) queue.shift()
  queue.push({ ...evt, sessionId })
}

/** Record a curated user action, e.g. track('jobs:export', { shape }). */
export function track(action: string, props?: Record<string, unknown>): void {
  try {
    enqueue({ type: 'action', action, module: currentModule, props })
  } catch { /* tracking never throws */ }
}

/** Record the content of an explicitly designated input (on blur/submit). */
export function trackInput(field: string, value: string): void {
  try {
    enqueue({ type: 'input', field, value: String(value).slice(0, 4096), module: currentModule })
  } catch { /* tracking never throws */ }
}

async function flush(final: boolean): Promise<void> {
  if (!queue.length) return
  const events = queue.splice(0, 500)
  try {
    const token = await getAuthToken()
    const base = import.meta.env.VITE_API_BASE_URL || ''
    // keepalive lets the final flush survive tab close (sendBeacon can't
    // carry the Authorization header, fetch keepalive can).
    void fetch(`${base}/api/usage/events`, {
      method: 'POST',
      keepalive: final,
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ events }),
    }).catch(() => { /* drop batch */ })
  } catch { /* drop batch */ }
}

function closeAndEnqueuePage(now: number): void {
  const closed = closePage(timer, now)
  if (closed && closed.seconds > 0) {
    enqueue({ type: 'page_view', module: closed.moduleId, seconds: closed.seconds })
  }
}

export function initUsageTracker(router: Router): void {
  if (installed || import.meta.env.VITE_USAGE_TRACKING === 'off') return
  installed = true
  try {
    router.afterEach((to) => {
      const name = typeof to.name === 'string' && to.name !== '__root'
        ? String(to.meta.moduleId ?? to.name)
        : null
      // Tab-level identity: '<module>:<tab>' so in-module tab switches are
      // distinct pages ('production-scheduler:jobs' vs ':capacity').
      const tab = typeof to.params.tab === 'string' && to.params.tab ? `:${to.params.tab}` : ''
      const ident = name ? `${name}${tab}` : null
      if (ident === currentModule) return
      const now = Date.now()
      closeAndEnqueuePage(now)
      currentModule = ident
      timer = startPage(ident, now, document.visibilityState === 'visible')
    })
    document.addEventListener('visibilitychange', () => {
      timer = setVisibility(timer, document.visibilityState === 'visible', Date.now())
    })
    window.addEventListener('pagehide', () => {
      closeAndEnqueuePage(Date.now())
      void flush(true)
    })
    window.setInterval(() => { void flush(false) }, FLUSH_MS)
  } catch { /* tracking must never break boot */ }
}
